<template>
  <div class="survey-setting">
    <div v-if="step === 1"
         class="step step-1">
      <div class="setting-header">
        <i class="icon icon--close-size16"
           @click="onCloseAll"></i>

        <div class="setting-title"
             v-html="$t('journey.targeting.survey.step1.title')"></div>
        <div class="setting-description"
             v-html="$t('journey.targeting.survey.step1.description')"></div>
      </div>
      <div class="setting-body">
        <div class="thead">
          <table :style="{ width: `calc(100% - ${scrollbarWidth + 30}px)` }">
            <thead>
              <tr>
                <th class="name">
                  {{ $t('journey.targeting.survey.step1.name') }}
                </th>
                <th class="date">
                  {{ $t('journey.targeting.survey.step1.date') }}
                </th>
                <th class="count">
                  {{ $t('journey.targeting.survey.step1.responses') }}
                </th>
                <th class="status">
                  {{ $t('journey.targeting.survey.step1.status') }}
                </th>
              </tr>
            </thead>
          </table>
        </div>
        <div class="tbody">
          <table v-if="surveys">
            <tbody>
              <tr v-for="survey in surveys"
                  :key="survey._id"
                  @click="selectSurvey(survey)">
                <td class="name">
                  <span class="cell">
                    <ui-radio :value="journeyOptions.survey && journeyOptions.survey.surveyId === survey.surveyId" />
                    {{ survey.surveyName }}
                  </span>
                </td>
                <td class="date">
                  {{ computedDate(survey.startDate) }}
                  ~
                  {{ computedDate(survey.endDate) }}
                </td>
                <td class="count">
                  {{ survey.answerCount | thousand }}
                </td>
                <td class="status"
                    :class="survey.status">
                  {{ $t(`userResearch.status.${survey.status}`) }}
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="surveys && surveys.length < 1"
               class="survey-empty">
            <i class="icon loading--empty"></i><br>
            {{ $t('common.noCollectedSurvey') }}
          </div>

          <transition name="fade">
            <div v-if="!surveys"
                 class="loader">
              <ba-loading :applyBackground="false"
                          :showLoadingText="true"/>
            </div>
          </transition>
        </div>
      </div>
      <div class="setting-footer">
        <button class="back-button"
                @click="onCloseLayer">
          <i class="icon icon--arrow-left-gray"></i>
          {{ $t('common.back') }}
        </button>
      </div>
    </div>
    <div v-if="step === 2"
         class="step step-2">
      <div class="setting-header">
        <i class="icon icon--close-size16"
           @click="onCloseLayer"></i>
        <div class="setting-title"
             v-html="$t('journey.targeting.survey.step2.title')"></div>
        <div class="setting-description"
             v-html="$t('journey.targeting.survey.step2.description')"></div>
      </div>
      <div class="setting-body">
        <div class="setting-segment">
          <template v-if="selectedSurvey">
            <div class="survey-title">
              {{ selectedSurvey.surveyName }}
            </div>

            <div v-if="summarySurvey"
                 class="survey-options">
              <div class="selected">
                <span class="number">
                  {{ segmentItemCount | thousand }}
                </span>
                {{ $t('common.selected') }}
              </div>
              <div class="not-target">
                <span :class="{ disabled: totalCount - summarySurvey.totalExposed < 1 }">
                  <ui-checkbox :value="selectedSegment.notTarget"
                               @input="checkSegment('notTarget')">
                    <span class="label">
                      {{ $t('journey.targeting.survey.step2.notTarget') }}
                    </span>
                    <span class="count">
                      ({{ getCount(totalCount - summarySurvey.totalExposed) }}{{ $t('common.unit.people') }}/{{ getPercentage(totalCount - summarySurvey.totalExposed, totalCount) }}%)
                    </span>
                  </ui-checkbox>
                </span>
              </div>
              <div class="target">
                <span :class="{ disabled: summarySurvey.totalExposed < 1 }">
                  <ui-checkbox :value="allAnswered && selectedSegment.notAnswered"
                               :unselect="allAnswered || selectedSegment.notAnswered || !!selectedSegment.answerOptions"
                               @input="checkSegment('target')">
                    <span class="label">
                      {{ $t('journey.targeting.survey.step2.target') }}
                    </span>
                    <span class="count">
                      ({{ getCount(summarySurvey.totalExposed) }}{{ $t('common.unit.people') }}/{{ getPercentage(summarySurvey.totalExposed, totalCount) }}%)
                    </span>
                  </ui-checkbox>
                </span>

                <ul class="stair">
                  <li>
                    <span :class="{ disabled: summarySurvey.totalExposed - summarySurvey.totalAnswered < 1 }">
                      <ui-checkbox :value="selectedSegment.notAnswered"
                                   @input="checkSegment('notAnswered')">
                        <span class="label">
                          {{ $t('journey.targeting.survey.step2.notAnswered') }}
                        </span>
                        <span class="count">
                          ({{ getCount(summarySurvey.totalExposed - summarySurvey.totalAnswered) }}{{ $t('common.unit.people') }}/{{ getPercentage(summarySurvey.totalExposed - summarySurvey.totalAnswered, totalCount) }}%)
                        </span>
                      </ui-checkbox>
                    </span>
                  </li>
                  <li>
                    <span :class="{ disabled: summarySurvey.totalAnswered < 1 }">
                      <ui-checkbox :value="allAnswered"
                                   :unselect="!!selectedSegment.answerOptions"
                                   @input="checkSegment('answered')">
                        {{ $t('journey.targeting.survey.step2.answered') }}
                        <span class="count">
                          ({{ getCount(summarySurvey.totalAnswered) }}{{ $t('common.unit.people') }}/{{ getPercentage(summarySurvey.totalAnswered, totalCount) }}%)
                        </span>
                      </ui-checkbox>
                  </span>

                    <ul class="questions">
                      <li v-for="(question, i) in summarySurvey.statsPerQuestion"
                          :key="`question-${i}`"
                          :class="{ active: opened.includes(i) }"
                          class="question">
                          <ui-tooltip v-if="!isCurrentPlanPro" arrowBottom arrowCenter>
                            <span v-html="$t('plan.surveyTooltip')"></span>
                          </ui-tooltip>
                          <span :class="{ disabled: (question.totalSession || question.totalAnswered) < 1 }">
                          <ui-checkbox :value="selectedSegment.answerOptions && selectedSegment.answerOptions[question.questionId] && (
                                          (question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_TEXT || (question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_RADIO || question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_CHECKBOX) && selectedSegment.answerOptions[question.questionId].length >= Object.keys(question.answerOptions).filter(answer => ((question.answers[answer] || 0) > 0)).length) ||
                                          (question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_RANGE && selectedSegment.answerOptions[question.questionId].length >= makeArray(question).length))"
                                       :unselect="selectedSegment.answerOptions && selectedSegment.answerOptions[question.questionId] && question.qustionType !== SURVEY_CONSTANT.ITEM_TYPE_TEXT && selectedSegment.answerOptions[question.questionId].length > 0"
                                       @input="isCurrentPlanPro ? checkSegment('question', question) : ''"
                                       :disabled="!isCurrentPlanPro">
                          </ui-checkbox>
                          <div class="label-wrap"
                               :class="question.qustionType"
                               :style="{opacity: isCurrentPlanPro ? 1 : 0.3}"
                               @click="openOption(i)">
                            <span class="label">
                              <i v-if="question.qustionType !== SURVEY_CONSTANT.ITEM_TYPE_TEXT"
                                 class="icon icon--arrow-dropdown-full"></i>
                              {{ question.question }}
                            </span>
                            <span class="count">
                              ({{ getCount((question.totalSession || question.totalAnswered)) }}{{ $t('common.unit.people') }}/{{ getPercentage((question.totalSession || question.totalAnswered), totalCount) }}%)
                            </span>
                          </div>
                        </span>

                        <ul v-if="question.qustionType !== SURVEY_CONSTANT.ITEM_TYPE_TEXT"
                            class="answers"
                            :style="{ 'max-height' : (question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_RANGE ? makeArray(question, true) : Object.keys(question.answerOptions)).length * 100 + 'px' }">
                          <li v-for="(answer, j) in (question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_RANGE ? makeArray(question, true) : Object.keys(question.answerOptions))"
                              :key="`answer-${i}-${j}`"
                              class="answer">
                            <span :class="{ disabled: (question.answers[answer] || 0) < 1 }">
                              <ui-checkbox :value="selectedSegment.answerOptions && selectedSegment.answerOptions[question.questionId] && selectedSegment.answerOptions[question.questionId].includes(answer)"
                                           :disabled="!isCurrentPlanPro"
                                           @input="isCurrentPlanPro ? checkSegment('answer', question, answer) : ''">
                                <span v-if="question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_RADIO || question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_CHECKBOX"
                                      class="label">
                                  {{ question.answerOptions[answer] }}
                                </span>
                                <span v-else-if="question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_RANGE"
                                      class="label">
                                  {{ answer }}
                                </span>
                                <span class="count">
                                  ({{ getCount(question.answers[answer] || 0) }}{{ $t('common.unit.people') }}/{{ getPercentage(question.answers[answer] || 0, totalCount) }}%)
                                </span>
                              </ui-checkbox>
                            </span>
                          </li>
                        </ul>
                      </li>
                    </ul>
                  </li>
                </ul>
              </div>
            </div>

            <transition name="fade">
              <div v-if="!summarySurvey"
                   class="loader">
                <ba-loading :applyBackground="false"
                            :showLoadingText="true"/>
              </div>
            </transition>
          </template>
        </div>
      </div>
      <div class="setting-footer">
        <button class="back-button"
                @click="toStep(1)">
          <i class="icon icon--arrow-left-gray"></i>
          {{ $t('common.back') }}
        </button>
        <ui-button @click="resetSegment"
                   :iconName="'icon--reset'"
                   :disabled="segmentCount < 1">
          {{ $t('common.reset') }}
        </ui-button>
        <ui-button @click="setSurvey"
                   :primary2="true"
                   :disabled="segmentCount < 1">
          {{ $t('common.apply') }}
        </ui-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import { format, isBefore, isAfter, endOfDay, startOfDay } from 'date-fns'
import { getSurveyList, getResponseSummary } from '@/api/survey'
import { getPercentage, thousand, utcToLocalTimezone } from '@/libs/utils'

import UiButton from '@/components/ui/UiButton'
import UiCheckbox from '@/components/ui/UiCheckbox'
import UiRadio from '@/components/ui/UiRadio'
import UiTooltip from '@/components/ui/UiTooltip'
import BaLoading from '@/components/ui/ba/Loading'
import { SURVEY_CONSTANT } from '@/libs/constants'

export default {
  name: 'SurveySetting',
  components: {
    UiButton,
    UiCheckbox,
    UiRadio,
    UiTooltip,
    BaLoading
  },
  props: ['sessionCount', 'sessionTotalCount'],
  created () {
    this.loadList()
  },
  methods: {
    ...mapMutations([
      'setSurveyList'
    ]),
    computedDate (date) {
      return format(utcToLocalTimezone(date, this.domain.timezoneName), 'YYYY-MM-DD')
    },
    toStep (step) {
      this.step = step
    },
    loadList () {
      const statusList = [SURVEY_CONSTANT.STATUS_RUNNING, SURVEY_CONSTANT.STATUS_WAITING, SURVEY_CONSTANT.STATUS_STOP, SURVEY_CONSTANT.STATUS_END]
      getSurveyList({ sid: this.sid }).then(result => {
        this.setSurveyList([this.sid, result])
        this.surveys = []
        for (const survey of result) {
          if (survey.status === SURVEY_CONSTANT.STATUS_WAITING) {
            survey.status = SURVEY_CONSTANT.STATUS_RUNNING
          }
          const startDate = utcToLocalTimezone(survey.startDate, this.domain.timezoneName)
          const endDate = utcToLocalTimezone(survey.endDate, this.domain.timezoneName)
          const journeyStartDate = startOfDay(this.journeyPeriod.start)
          const journeyEndDate = endOfDay(this.journeyPeriod.end)
          if (statusList.includes(survey.status)) {
            if (isAfter(startDate, journeyEndDate)) {
              continue
            }
            if (isBefore(endDate, journeyStartDate)) {
              continue
            }

            this.surveys.push(survey)
          }

          if (this.journeyOptions.survey && this.journeyOptions.survey.surveyId === survey.surveyId) {
            this.selectedSurvey = survey
          }
        }

        this.surveys.sort((a, b) => {
          const sortByStartDate = new Intl.Collator('en').compare(format(b.startDate, 'YYYY-MM-DD'), format(a.startDate, 'YYYY-MM-DD'))
          if (!sortByStartDate) {
            if (a.status === b.status) {
              const sortByRegDate = new Intl.Collator('en').compare(b.regDate.toString(), a.regDate.toString())
              return sortByRegDate
            } else {
              return statusList.indexOf(a.status) > statusList.indexOf(b.status) ? 1 : -1
            }
          }
          return sortByStartDate
        })
      })
    },
    loadSummary () {
      if (this.selectedSurvey) {
        this.summarySurvey = null
        getResponseSummary({
          sid: this.sid,
          surveyId: this.selectedSurvey.surveyId,
          start: this.journeyPeriod.start,
          end: this.journeyPeriod.end
        }, false).then(result => {
          this.summarySurvey = result
          for (const q of Object.entries(this.summarySurvey.statsPerQuestion)) {
            const id = q[0]
            this.summarySurvey.statsPerQuestion[id].questionId = id
            this.summarySurvey.statsPerQuestion[id].tootip = !this.isCurrentPlanPro

            if (typeof this.summarySurvey.statsPerQuestion[id].answers['None'] === 'number') {
              this.summarySurvey.statsPerQuestion[id].answerOptions['None'] = this.$t('userResearch.survey.create.other')
            }
          }

          if (this.journeyOptions.survey && this.journeyOptions.survey.surveyId === this.selectedSurvey.surveyId) {
            this.selectedSegment = { ...this.journeyOptions.survey }
            delete this.selectedSegment.surveyId
          }
        })
      }
    },
    onCloseLayer () {
      this.$emit('onCloseLayer')
    },
    onCloseAll () {
      this.$emit('onCloseAll')
    },
    selectSurvey (survey) {
      this.selectedSurvey = survey
      this.selectedSegment = {}
      this.opened = []
      this.loadSummary()
      this.step = 2
    },
    openOption (key) {
      if (this.opened.includes(key)) {
        this.opened.splice(this.opened.indexOf(key), 1)
      } else {
        this.opened.push(key)
      }
    },
    getPercentage (value, total) {
      return getPercentage(value, total).toFixed(0)
    },
    getCount (value) {
      if (value >= 10000) {
        return thousand(Math.round(value / 1000)) + 'K'
      }
      return thousand(value)
    },
    makeArray (question, all = false) {
      let array = [...Array(question.answerOptions.maxValue - question.answerOptions.minValue + 1).keys()].map(x => String(question.answerOptions.minValue + x++))
      if (!all) {
        array = array.filter(answer => ((question.answers[answer] || 0) > 0))
      }
      return array
    },
    resetSegment () {
      this.selectedSegment = {}
    },
    checkSegment (type, question = {}, answer = null) {
      if (!this.selectedSegment.answerOptions) {
        this.$set(this.selectedSegment, 'answerOptions', {})
      }
      if (type === 'notTarget') {
        if (this.totalCount - this.summarySurvey.totalExposed > 0) {
          if (this.selectedSegment.notTarget) {
            this.$delete(this.selectedSegment, 'notTarget')
          } else {
            this.$set(this.selectedSegment, 'notTarget', true)
          }
        }
      } else if (type === 'target') {
        if (this.summarySurvey.totalExposed > 0) {
          if (this.selectedSegment.notAnswered && this.allAnswered) {
            this.$delete(this.selectedSegment, 'notAnswered')
            this.$set(this.selectedSegment, 'answerOptions', {})
          } else {
            this.$set(this.selectedSegment, 'notAnswered', true)
            for (const question of Object.values(this.summarySurvey.statsPerQuestion)) {
              if (question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_TEXT) {
                this.$set(this.selectedSegment.answerOptions, question.questionId, true)
              } else if (question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_RADIO || question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_CHECKBOX) {
                const answers = Object.keys(question.answerOptions).filter(answer => ((question.answers[answer] || 0) > 0))
                this.$set(this.selectedSegment.answerOptions, question.questionId, answers)
              } else if (question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_RANGE) {
                this.$set(this.selectedSegment.answerOptions, question.questionId, this.makeArray(question))
              }
            }
          }
        }
      } else if (type === 'notAnswered') {
        if (this.summarySurvey.totalExposed - this.summarySurvey.totalAnswered > 0) {
          if (this.selectedSegment.notAnswered) {
            this.$delete(this.selectedSegment, 'notAnswered')
          } else {
            this.$set(this.selectedSegment, 'notAnswered', true)
          }
        }
      } else if (type === 'answered') {
        if (this.summarySurvey.totalAnswered > 0) {
          if (this.allAnswered) {
            this.$set(this.selectedSegment, 'answerOptions', {})
          } else {
            for (const question of Object.values(this.summarySurvey.statsPerQuestion)) {
              if (question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_TEXT) {
                this.$set(this.selectedSegment.answerOptions, question.questionId, true)
              } else if (question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_RADIO || question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_CHECKBOX) {
                const answers = Object.keys(question.answerOptions).filter(answer => ((question.answers[answer] || 0) > 0))
                this.$set(this.selectedSegment.answerOptions, question.questionId, answers)
              } else if (question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_RANGE) {
                this.$set(this.selectedSegment.answerOptions, question.questionId, this.makeArray(question))
              }
            }
          }
        }
      } else if (type === 'question') {
        if ((question.totalSession || question.totalAnswered) > 0) {
          if (this.selectedSegment.answerOptions[question.questionId]) {
            if (question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_TEXT) {
              this.$delete(this.selectedSegment.answerOptions, question.questionId)
            } else if (question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_RADIO || question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_CHECKBOX) {
              const answers = Object.keys(question.answerOptions).filter(answer => ((question.answers[answer] || 0) > 0))
              if (this.selectedSegment.answerOptions[question.questionId].length < answers.length) {
                this.$set(this.selectedSegment.answerOptions, question.questionId, answers)
              } else {
                this.$delete(this.selectedSegment.answerOptions, question.questionId)
              }
            } else if (question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_RANGE) {
              if (this.selectedSegment.answerOptions[question.questionId].length < this.makeArray(question).length) {
                this.$set(this.selectedSegment.answerOptions, question.questionId, this.makeArray(question))
              } else {
                this.$delete(this.selectedSegment.answerOptions, question.questionId)
              }
            }
          } else {
            if (question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_TEXT) {
              this.$set(this.selectedSegment.answerOptions, question.questionId, true)
            } else if (question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_RADIO || question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_CHECKBOX) {
              const answers = Object.keys(question.answerOptions).filter(answer => ((question.answers[answer] || 0) > 0))
              this.$set(this.selectedSegment.answerOptions, question.questionId, answers)
            } else if (question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_RANGE) {
              this.$set(this.selectedSegment.answerOptions, question.questionId, this.makeArray(question))
            }
          }
        }
      } else if (type === 'answer') {
        if ((question.answers[answer] || 0) > 0) {
          if (this.selectedSegment.answerOptions[question.questionId]) {
            if (question.qustionType !== SURVEY_CONSTANT.ITEM_TYPE_TEXT) {
              const answers = [...this.selectedSegment.answerOptions[question.questionId]]
              if (answers.includes(answer)) {
                const answers = [...this.selectedSegment.answerOptions[question.questionId]]
                answers.splice(answers.indexOf(answer), 1)
                if (answers.length > 0) {
                  this.$set(this.selectedSegment.answerOptions, question.questionId, answers)
                } else {
                  this.$delete(this.selectedSegment.answerOptions, question.questionId)
                }
              } else {
                answers.push(answer)
                this.$set(this.selectedSegment.answerOptions, question.questionId, answers)
              }
            } else {
              this.$set(this.selectedSegment.answerOptions, question.questionId, true)
            }
          } else {
            if (question.qustionType !== SURVEY_CONSTANT.ITEM_TYPE_TEXT) {
              this.$set(this.selectedSegment.answerOptions, question.questionId, [answer])
            } else {
              this.$set(this.selectedSegment.answerOptions, question.questionId, false)
            }
          }
        }
      }
      if (Object.keys(this.selectedSegment.answerOptions).length < 1) {
        this.$delete(this.selectedSegment, 'answerOptions')
      }
    },
    setSurvey () {
      this.onCloseAll()
      this.$emit('onSetSurvey', {
        ...this.selectedSegment,
        surveyId: this.selectedSurvey.surveyId
      })
    }
  },
  computed: {
    ...mapState([
      'journeyPeriod',
      'journeyOptions',
      'scrollbarWidth',
      'domain',
      'planData'
    ]),
    sid () {
      return this.$route.query.sid || this.domain.orgSid
    },
    segmentCount () {
      return Object.keys(this.selectedSegment).length
    },
    segmentItemCount () {
      let count = 0
      if (this.selectedSegment.notTarget) {
        count++
      }
      if (this.selectedSegment.notAnswered) {
        count++
      }
      if (this.selectedSegment.answerOptions) {
        for (const answer of Object.values(this.selectedSegment.answerOptions)) {
          if (answer === true) {
            count++
          } else {
            count += answer.length
          }
        }
      }
      return count
    },
    totalCount () {
      return this.sessionTotalCount || 0.000001
    },
    allAnswered () {
      if (!this.selectedSegment.answerOptions) {
        return false
      }
      if (Object.keys(this.selectedSegment.answerOptions).length < Object.keys(this.summarySurvey.statsPerQuestion).length) {
        return false
      }
      for (const question of Object.values(this.summarySurvey.statsPerQuestion)) {
        if (question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_RADIO || question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_CHECKBOX) {
          const answers = Object.keys(question.answerOptions).filter(answer => ((question.answers[answer] || 0) > 0))
          if (this.selectedSegment.answerOptions[question.questionId].length < answers.length) {
            return false
          }
        } else if (question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_RANGE) {
          if (this.selectedSegment.answerOptions[question.questionId].length < this.makeArray(question).length) {
            return false
          }
        } else if (question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_TEXT) {
          if (!this.selectedSegment.answerOptions[question.questionId]) {
            return false
          }
        }
      }
      return true
    },
    isCurrentPlanPro () {
      return !this.planData.bFree
    }
  },
  data () {
    return {
      SURVEY_CONSTANT: SURVEY_CONSTANT,
      step: 1,
      surveys: null,
      selectedSurvey: null,
      summarySurvey: null,
      selectedSegment: {},
      opened: []
    }
  }
}
</script>

<style lang="scss" scoped>
.survey-setting {
  position: relative;
  width: 830px;
  height: 543px;
  border-radius: 14px;
  box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
  background-color: #fff;

  .step {
    padding: 30px;
  }

  .icon--close-size16 {
    position: absolute;
    top: 12px;
    right: 12px;
  }

  .setting-header {
    height: 70px;

    .setting-title {
      font-size: 20px;
      font-weight: 600;
      color: #444;
    }

    .setting-description {
      font-size: 13px;
      line-height: 1.08;
      color: #888;
      margin-top: 12px;
    }
  }

  .setting-body {
    height: 370px;

    .tbody {
      position: relative;
      height: 333px;
      border-radius: 5px;
      border: solid 1px #d7d7d7;
      background-color: #f4f4f4;
      overflow-y: scroll;
      overflow-x: hidden;
    }

    table {
      width: calc(100% - 30px);
      margin: 0 10px 0 20px;

      th {
        font-size: 13px;
        font-weight: 600;
        line-height: 1.23;
        letter-spacing: normal;
        text-align: center;
        color: #767676;
        padding: 12px 0 8px;

        &.name {
          text-align: left;
          padding-left: 10px;
        }

        &.date {
          width: 195px;
        }

        &.count {
          width: 92px;
          padding-right: 23px;
          text-align: right;
        }

        &.status {
          width: 90px;
        }
      }

      td {
        text-align: center;
        font-size: 13px;
        line-height: 1.23;
        letter-spacing: normal;
        color: #444444;
        padding: 16px 0;
        border-bottom: 1px solid #ebebeb;
        cursor: pointer;

        &.name {
          text-align: left;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          padding: 13px 0;

          .cell {
            padding: 3px 0 3px 10px;
            display: block;
            width: 300px;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
          }

          .ui_radio {
            margin: 0 10px 0 -10px;
          }
        }

        &.date {
          width: 195px;
        }

        &.count {
          width: 92px;
          padding-right: 23px;
          text-align: right;
        }

        &.status {
          width: 90px;
          color: #999999;

          &.run {
            color: #57ab00;
          }

          &.stop {
            color: #767676;
          }
        }
      }
    }

    .survey-empty {
      padding-top: 85px;
      height: 148px;
      text-align: center;
      line-height: 1.46;
      font-size: 13px;
      color: #555555;

      i {
        margin-top: -23px;
      }
    }

    .loader {
      position: absolute;
      z-index: 100;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 100%;
      height: 100%;
      border-radius: 5px;
      background-color: #f4f4f4;

      &.fade-enter-active, &.fade-leave-active {
        opacity: 1;
        transition: opacity 0.2s ease;
      }

      &.fade-enter, &.fade-leave-to{
        opacity: 0;
      }
    }

    .setting-segment {
      position: relative;
      height: 352px;
      border-radius: 5px;
      border: solid 1px #d7d7d7;
      background-color: #f4f4f4;
      overflow: auto;

      .survey-title {
        font-size: 16px;
        font-weight: 600;
        line-height: 1.31em;
        color: #444;
        margin: 20px;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        max-height: 2.62em;
      }

      .survey-options {
        position: relative;

        .selected {
          position: absolute;
          top: 10px;
          right: 26px;
          font-size: 13px;
          line-height: 1.38;
          color: #555;

          .number {
            font-size: 15px;
            font-weight: bold;
            line-height: 1.2;
            color: #ec0047;
          }
        }

        .not-target {
          padding: 12px 0 21px;
          margin: 0 20px;
          border-bottom: 1px solid #e2e2e2;
        }

        .target {
          margin: 0 20px;
          padding: 20px 0 21px;
        }

        .stair {
          padding: 25px 0 21px 10px;

          & > li:first-child {
            padding-bottom: 19px;
          }
        }

        .questions {
          padding: 15px 5px 0 0;

          .question {
            padding: 11px;
            margin: 7px 0 0 0;
            border-radius: 4px;
            background-color: #e5e5e5;
            position: relative;

            .ui_checkbox {
              position: relative;
              top: 2px;
              margin-left: 0;
              vertical-align: top;
            }

            .label-wrap {
              display: inline-block;
              width: calc(100% - 25px);
              color: #444;
              font-size: 12px;
              color: #444;

              &.range,
              &.radio,
              &.checkbox {
                cursor: pointer;
              }
            }

            .label {
              position: relative;
              display: inline-block;
              width: calc(100% - 140px);
              font-size: 12px;
              vertical-align: top;
              line-height: 1.5;
              margin-left: 15px;
              transition: opacity 0.2s ease;

              .icon--arrow-dropdown-full {
                position: absolute;
                top: 2px;
                left: -17px;
              }
            }

            .count {
              float: right;
              padding-right: 3px;
              line-height: 1.5;
              transition: opacity 0.2s ease;
            }

            &.active {
              .answers {
                margin-top: 12px;
                height: auto;
                opacity: 1;
              }

              .icon--arrow-dropdown-full {
                transform: scaleY(-1);
              }
            }

            &:not(.active) {
              .answers {
                max-height: 0 !important;
              }
            }

            &:hover {
            .bubble {
              display: block;
            }
          }
          span {
              &.blur {
                opacity: 0.5;
              }
            }
          }
          & /deep/ .bubble {
            top: -50px;
            left: -25px;
            display: none;
          }
          & /deep/ .arrow {
            left: 13%;
          }
        }

        .answers {
          overflow: hidden;
          margin-top: 0;
          opacity: 0;
          max-height: 0;
          height: 0;
          transition: height 0.2s ease,
                      opacity 0.2s ease,
                      max-height 0.2s ease,
                      margin-top 0.2s ease;

          &.hidden {
            margin-top: 0;
            height: 0;
            opacity: 0;
          }

          .answer {
            padding: 14px 0 6px 0;
            margin-left: 22px;

            &:first-child {
              border-top: dashed 1px #bcbcbc;
            }

            .label {
              margin-left: 0;
              width: calc(100% - 160px);
            }
          }
        }

        .ui_checkbox {
          margin-left: 0;

          & /deep/ {
            .text {
              font-size: 13px;
              color: #444;

              label {
                display: inline-block;
                max-width: calc(100% - 200px);
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
              }

              .count {
                display: inline-block;
              }
            }
          }
        }
      }
    }
  }

  .setting-footer {
    position: relative;
    text-align: center;
    padding-top: 10px;

    .back-button {
      position: absolute;
      top: 10px;
      left: 0;
      vertical-align: middle;
      height: 38px;
      cursor: pointer;
      padding-bottom: 6px;
      font-weight: 600;
      color: #444;
      font-size: 14px;

      i {
        vertical-align: middle;
        margin: -3px 3px 0 0;
      }
    }

    .ui-btn {
      width: 90px;
      margin: 0 2px 0 3px;
    }
  }
}
</style>
